import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { token } from '@/lib/tokens';
import 'leaflet/dist/leaflet.css';

interface Props {
  lat: number;
  lng: number;
  radiusM: number;
  /** 可拖动图钉改坐标。只读模式（工地详情展示）不传。 */
  onMove?: (lat: number, lng: number) => void;
  heightClass?: string;
}

/**
 * 打卡围栏地图。
 *
 * 存在的理由：管理员在输入框里填「150」看不出这到底覆盖多大范围。
 * 把圆画在卫星底图上，一眼就能看出围栏是刚好罩住工地，还是把隔壁马路
 * 也圈进来了 —— 半径设得过小会让工人站在工地里打不上卡，
 * 设得过大等于没有约束。
 *
 * 用 OpenStreetMap 而不是 Google Maps：免费、不需要 API Key、不用绑信用卡。
 */
export function GeofenceMap({ lat, lng, radiusM, onMove, heightClass = 'h-64' }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;

  // 初始化一次。坐标和半径的后续变化走下面那个 effect，不重建地图。
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: [lat, lng],
      zoom: 17,
      // 工地地图是给人看范围的，不需要能缩到全球
      minZoom: 12,
      maxZoom: 19,
      attributionControl: true,
      // 地图嵌在长页面中间（工地设置，CR-082）：滚轮留给页面，缩放用左上角的 +/−
      scrollWheelZoom: false,
    });

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    }).addTo(map);

    // 色值从 token 读，别在这里抄 hex —— 抄了之后改配色会漏掉地图，
    // 界面换了色而围栏还是旧颜色，而且没有任何报错提示你
    const circle = L.circle([lat, lng], {
      radius: radiusM,
      color: token('--brand-600'),
      weight: 2,
      fillColor: token('--brand-500'),
      fillOpacity: 0.15,
    }).addTo(map);

    // 用 divIcon 而不是默认图标：Leaflet 默认图标的图片路径经过打包器会失效，
    // 自己画一个圆点省掉这个坑
    const icon = L.divIcon({
      className: '',
      html:
        '<div style="width:16px;height:16px;border-radius:50%;' +
        `background:${token('--safety-500')};` +
        `border:3px solid ${token('--surface')};` +
        `box-shadow:${token('--shadow-marker')}"></div>`,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });

    const marker = L.marker([lat, lng], { icon, draggable: Boolean(onMoveRef.current) }).addTo(map);

    marker.on('dragend', () => {
      const pos = marker.getLatLng();
      circleRef.current?.setLatLng(pos);
      onMoveRef.current?.(pos.lat, pos.lng);
    });

    // 点地图也能挪中心点，比拖那个小圆点好点中
    map.on('click', (event: L.LeafletMouseEvent) => {
      if (!onMoveRef.current) return;
      marker.setLatLng(event.latlng);
      circle.setLatLng(event.latlng);
      onMoveRef.current(event.latlng.lat, event.latlng.lng);
    });

    mapRef.current = map;
    circleRef.current = circle;
    markerRef.current = marker;

    return () => {
      map.remove();
      mapRef.current = null;
      circleRef.current = null;
      markerRef.current = null;
    };
    // 依赖刻意留空：地图只建一次。坐标和半径的后续变化由下面那个 effect
    // 增量同步，重建地图会丢掉用户的缩放和平移。
  }, []);

  // 外部改了坐标或半径（表单输入、点「用当前位置」）时同步到地图
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !circleRef.current || !markerRef.current) return;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const position: L.LatLngExpression = [lat, lng];
    circleRef.current.setLatLng(position);
    circleRef.current.setRadius(Math.max(1, radiusM));
    markerRef.current.setLatLng(position);

    // 半径变化时自动调整视野，让整个圆都看得见
    map.fitBounds(circleRef.current.getBounds(), { padding: [24, 24], maxZoom: 18 });
  }, [lat, lng, radiusM]);

  return (
    <div
      ref={containerRef}
      className={`${heightClass} w-full overflow-hidden rounded-md border border-rule`}
      // Leaflet 的瓦片层级和页面里的粘性侧栏会打架，压一下
      style={{ zIndex: 0 }}
      role="application"
      aria-label="打卡围栏范围"
    />
  );
}
